/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { _T } from "@rfice/language";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useEventHandlers } from "@rfice/eventhandler";
import { usePushNotification } from "@/frameworks/libraries/hooks";
import { useStompClientActions } from "@/frameworks/libraries/network";
import { avatarSettingScene, useUnitySystemActions } from "@/frameworks/core/unity";
import {
	useNotificationManager,
	useMydeskNotificationManager,
	useRoomViewActions,
	useConfirm
} from "@/frameworks/core/hooks";
import useScene from "@/frameworks/core/hooks/useScene";
import { Configure, Properties } from "@/frameworks/libraries";
import { EventID } from "../../commons";
import { useNetworkManager } from "./private";
import serviceHeartbeatStatic from "../../utils/ServiceHeartbeat/ServiceHeartbeat";
import { usePersistActions } from "../../persistence";
import { useMeeting } from "../../API";

const LOGTAG = "#useServiceInitialize#";

export default function useServiceInitialize() {
	const unitySystemActions = useUnitySystemActions();
	const stompClientActions = useStompClientActions();
	const network = useNetworkManager();
	const roomViewActions = useRoomViewActions();
	const location = useLocation();
	const { getScene } = useScene();
	const meetingAPI = useMeeting();
	const { confirm } = useConfirm();

	const pushNotification = usePushNotification();
	const notificationManager = useNotificationManager({
		onError(error) {
			console.error(LOGTAG, "notificationManager error", error);
		}
	});
	const mydeskNotificationManager = useMydeskNotificationManager({
		onError(error) {
			console.error(LOGTAG, "mydeskNotificationManager error", error);
		}
	});

	const checkIsMeetingMember = async (conferenceId: string) => {
		const me = await usePersistActions().users.refresh_user(Configure.userID);
		const meeting = await meetingAPI.meeting.detail({ conferenceId, timezone: me?.timezone || dayjs.tz.guess() });
		return meeting?.me?.invited || false;
	};

	useEventHandlers({
		[EventID.EXEC_UNITY_PLAYER]() {
			serviceHeartbeatStatic.start("fe_unity_service_init_health", 1000, "worker", () => {
				console.log(LOGTAG, "serviceHeartbeatStatic_start_service_init_health is running...");
			});
		},
		[EventID.SLEEP_UNITY_PLAYER]() {
			serviceHeartbeatStatic.stop("fe_unity_service_init_health");
		},
		[EventID.CLOSE_UNITY_PLAYER]() {
			serviceHeartbeatStatic.stop("fe_unity_service_init_health");
		},
		onViewportReady() {
			// 서비스 동작간 네트워크가 정상작동 된 후, 유니티 동작
			stompClientActions.waitForConnected().then(async () => {
				if (location.state?.params?.isMeetingGuest) {
					const scene = await getScene();
					// Replace goDefault by common go function
					if ("id" in scene) {
						if (scene.privateMeeting) {
							const isInvited = await checkIsMeetingMember(scene.id);
							if (!Properties.property.isGuest && !isInvited) {
								confirm(
									{
										title: _T("초대받은 사용자만 참석할 수 있는 비공개 회의 입니다."),
										useOnlyConfirm: true
									},
									{
										onConfirm() {
											unitySystemActions.goMyDesk();
										}
									}
								);
								return;
							}
						}
					}
					unitySystemActions.go("system", scene, {
						mode: "changeSpace"
					});
					return;
				}

				/**
				 * @ref https://www.notion.so/166b072761ec8076a38cf2065ac1c2e2?pvs=4
				 */
				if (Properties.property.isUserStartViaSigninPage) {
					//// 그룹가입 + 로그인
					if (Properties.property.routerInfo) {
						unitySystemActions.go("system", Properties.property.routerInfo, {
							mode: "changeSpace"
						});
						return;
					}
					if (Properties.property.firstLogin) {
						unitySystemActions.go("system", avatarSettingScene, {
							mode: "changeSpace"
						});
					} else {
						unitySystemActions.goMyDesk().then(() => {
							console.debug(LOGTAG, "Unity System is turned off");
						});
					}
				} else {
					if (Configure.mydeskMode) {
						// if (states.get_service_mode() === "MY_DESK") {
						unitySystemActions.goMyDesk().then(() => {
							console.debug(LOGTAG, "Unity System is turned off");
						});
					} else {
						// } else if (states.get_service_mode() === "3D_SPACE") {
						//// 그룹가입 + 로그인
						if (Properties.property.routerInfo) {
							return unitySystemActions.go("system", Properties.property.routerInfo, {
								mode: "changeSpace"
							});
						} else {
							const scene = await getScene();
							unitySystemActions.go("system", scene as UserSpaceType, {
								mode: "changeSpace"
							});
						}
					}
				}
			});
		}
	});

	useEffect(() => {
		// clear guest session
		const onBeforeUnload = () => {
			if (Properties.property.isGuest) {
				Configure.clear_authenticated();
			}
		};
		const beforeUnloadEvent = "beforeunload";
		window.addEventListener(beforeUnloadEvent, onBeforeUnload);
		return () => {
			window.removeEventListener(beforeUnloadEvent, onBeforeUnload);
		};
	}, []);

	return () => {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise<boolean>(async (resolve, reject) => {
			try {
				pushNotification?.checkPermission();
				notificationManager.run();
				mydeskNotificationManager.run();
				roomViewActions.load();
				network.connect_stomp_server();
				resolve(true);
			} catch (error: unknown) {
				console.error(LOGTAG, "Exception in start with", error);
				reject(error);
			}
		});
	};
}
